import {
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  HStack,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { AiOutlineInfoCircle } from 'react-icons/ai'

import { t } from '@/lib/helpers'
import { WhtOption, WhtPercent } from '../../types'

const recordStyle = {
  gridColumn: '1/3',
  alignItems: 'stretch',
  borderBottom: '1px solid',
  borderColor: 'gray.200',
  mx: 6,
  py: 2,
}

const headStyle = {
  fontWeight: 'bold',
  fontSize: 'sm',
  color: 'gray.500',
  pb: 2,
  borderBottom: '1px solid',
  borderColor: 'gray.200',
}

export const formatMoney = (value: number) =>
  Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

type WhtOptionRowProps = {
  percent: WhtPercent
  label: string
  tooltip: string
  isChecked: boolean
  isDisabled?: boolean
  onToggle: (percent: WhtPercent) => void
}

const WhtOptionRow = ({
  percent,
  label,
  tooltip,
  isChecked,
  isDisabled,
  onToggle,
}: WhtOptionRowProps) => (
  <Flex align="center">
    <Checkbox
      isChecked={isChecked}
      onChange={() => onToggle(percent)}
      isDisabled={isDisabled}
      data-testid={`wht-checkbox-${percent}`}
    >
      <Text as="span" fontSize="sm">
        {label} ({percent}%)
      </Text>
    </Checkbox>
    <Tooltip label={tooltip} hasArrow placement="top">
      <Box
        as="span"
        ml={1}
        display="inline-flex"
        color="gray.500"
        tabIndex={0}
        aria-label={tooltip}
      >
        <AiOutlineInfoCircle />
      </Box>
    </Tooltip>
  </Flex>
)

export type CheckoutSummaryProps = {
  name: string
  net: number
  preVatTotal: number
  vat: number
  vatPercent: string
  // Total payable with no withholding applied
  total: number
  whtOptions: WhtOption[]
  selectedWht: WhtPercent
  onChangeWht: (percent: WhtPercent) => void
  proRateDiscount?: { label: string; value: number }
  validate?: () => Record<string, string>
  onErrors?: (errors: Record<string, string>) => void
  onSubmit: () => void
  isLoading?: boolean
  loadingText?: string
}

export const CheckoutSummary = (props: CheckoutSummaryProps) => {
  const {
    name,
    net,
    preVatTotal,
    vat,
    vatPercent,
    total,
    whtOptions,
    selectedWht,
    onChangeWht,
    proRateDiscount,
    validate = () => ({}),
    onErrors = () => {},
    onSubmit,
    isLoading,
    loadingText,
  } = props

  const whtOption = whtOptions.find((o) => o.percent === selectedWht)
  const netTotal = whtOption ? Math.max(whtOption.total_amount, 0) : total

  // Mutually exclusive: ticking the ticked box clears it
  const toggle = (percent: WhtPercent) =>
    onChangeWht(selectedWht === percent ? 0 : percent)

  const labelOf = (percent: WhtPercent) =>
    percent === 1
      ? t('views.features.packages.checkout.ewht_label')
      : t('views.features.packages.checkout.wht_label')

  const tooltipOf = (percent: WhtPercent) =>
    percent === 1
      ? t('views.features.packages.checkout.ewht_tooltip')
      : t('views.features.packages.checkout.wht_tooltip')

  // E.WHT (1%) above WHT (3%)
  const sortedOptions = [...whtOptions].sort((a, b) => a.percent - b.percent)

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="8px">
      <VStack p="6" pb="2" spacing="13px" alignItems="stretch">
        <Text fontSize="xl" fontWeight="bold">
          {t('views.features.packages.checkout.summary_title')}
        </Text>
      </VStack>
      <Grid
        fontSize="sm"
        gridTemplateColumns="1fr minmax(90px, 120px)"
        textAlign="right"
        pt="2"
      >
        <Text textAlign="left" pl="6" pt="4" {...headStyle}>
          {t('views.features.packages.checkout.col_item')}
        </Text>
        <Text pr="6" pt="4" {...headStyle}>
          {t('views.features.packages.checkout.col_total')}
        </Text>
        <HStack sx={{ ...recordStyle, justifyContent: 'space-between' }}>
          <Box textAlign="left" py="2">
            <Text fontWeight="bold">{name}</Text>
          </Box>
          <Text whiteSpace="pre">{formatMoney(net)}</Text>
        </HStack>
        <VStack spacing="2" sx={recordStyle}>
          <HStack
            spacing="4"
            sx={{ justifyContent: 'space-between', pt: 2, w: '100%' }}
          >
            <Text textAlign="left">
              {t('views.features.packages.checkout.pre_vat')}
            </Text>
            <Text whiteSpace="pre">{formatMoney(preVatTotal)}</Text>
          </HStack>
          <HStack
            spacing="4"
            sx={{ justifyContent: 'space-between', pb: 2, w: '100%' }}
          >
            <Text textAlign="left">VAT {vatPercent}</Text>
            <Text whiteSpace="pre">{formatMoney(vat)}</Text>
          </HStack>
        </VStack>

        <HStack
          spacing="4"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ ...recordStyle, py: 4 }}
          data-testid="wht-row"
        >
          <VStack alignItems="flex-start" spacing="2">
            <Text textAlign="left">
              {t('views.features.packages.checkout.wht_title')}
            </Text>
            {sortedOptions.map((option) => (
              <WhtOptionRow
                key={option.percent}
                percent={option.percent}
                label={labelOf(option.percent)}
                tooltip={tooltipOf(option.percent)}
                isChecked={selectedWht === option.percent}
                isDisabled={isLoading}
                onToggle={toggle}
              />
            ))}
          </VStack>
          <Text
            data-testid="wht-amount"
            sx={{
              color: whtOption ? 'red.400' : 'inherit',
              fontWeight: whtOption ? 'semibold' : 'normal',
              whiteSpace: 'pre',
            }}
          >
            {whtOption ? `- ${formatMoney(whtOption.wht)}` : '-'}
          </Text>
        </HStack>

        {proRateDiscount && (
          <Box sx={{ ...recordStyle, py: 2 }}>
            <HStack spacing="4" justifyContent="space-between">
              <Box textAlign="left">{proRateDiscount.label}</Box>
              <Box color="red.400" fontWeight="semibold" whiteSpace="pre">
                - {formatMoney(proRateDiscount.value)}
              </Box>
            </HStack>
          </Box>
        )}

        <HStack
          sx={{ gridColumn: '1/3', justifyContent: 'space-between', mx: 6 }}
        >
          <Text sx={{ textAlign: 'left', py: 2, fontWeight: 'bold' }}>
            {t('views.features.packages.checkout.net_total')}
          </Text>
          <Text
            data-testid="net-total"
            sx={{
              py: 2,
              fontSize: 'xl',
              fontWeight: 'bold',
              whiteSpace: 'pre',
            }}
          >
            {formatMoney(netTotal)}
          </Text>
        </HStack>

        <Grid gridColumn="1 / 3" textAlign="left" p="6" gap="4">
          <Button
            size="lg"
            colorScheme="brand"
            isLoading={isLoading}
            loadingText={loadingText}
            id="submit-payment-button"
            mt="4"
            onClick={() => {
              const errors = validate()
              if (Object.keys(errors).length === 0) {
                onErrors({})
                onSubmit()
                return
              }
              onErrors(errors)
            }}
          >
            {t('views.features.packages.checkout.submit')}
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}
